import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import BrandMark from "@/components/BrandMark";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    document.querySelector(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div
      className="fixed top-4 sm:top-5 inset-x-0 z-50 flex justify-center pointer-events-none"
      data-testid="site-header"
    >
      <div
        className={`pointer-events-auto transition-[box-shadow,background-color,transform] duration-200 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-[0_10px_32px_-16px_rgba(10,10,10,0.22)] scale-[0.985]"
            : "bg-white/70 backdrop-blur-md shadow-[0_4px_20px_-12px_rgba(10,10,10,0.15)]"
        } border border-[rgba(10,10,10,0.08)] rounded-full pl-4 pr-1.5 py-1.5 flex items-center gap-1 w-[calc(100%-2rem)] max-w-[740px]`}
      >
        <a
          href="#hero"
          onClick={scrollTo("#hero")}
          className="flex items-center gap-2 pr-2"
          data-testid="navbar-brand-link"
        >
          <BrandMark size="sm" />
        </a>

        <nav
          className="hidden sm:flex items-center gap-0 ml-auto"
          aria-label="Primary"
        >
          <a
            href="#what"
            onClick={scrollTo("#what")}
            className="nav-underline px-3 py-2 text-[13.5px] font-medium text-[#0a0a0a]/80 hover:text-[#0a0a0a]"
            data-testid="navbar-link-what"
          >
            What we do
          </a>
          <a
            href="#work"
            onClick={scrollTo("#work")}
            className="nav-underline px-3 py-2 text-[13.5px] font-medium text-[#0a0a0a]/80 hover:text-[#0a0a0a]"
            data-testid="navbar-link-work"
          >
            Work
          </a>
          <a
            href="#details"
            onClick={scrollTo("#details")}
            className="nav-underline px-3 py-2 text-[13.5px] font-medium text-[#0a0a0a]/80 hover:text-[#0a0a0a]"
            data-testid="navbar-link-details"
          >
            Details
          </a>
          <a
            href="#contact"
            onClick={scrollTo("#contact")}
            className="nav-underline px-3 py-2 text-[13.5px] font-medium text-[#0a0a0a]/80 hover:text-[#0a0a0a]"
            data-testid="navbar-link-contact"
          >
            Contact
          </a>
        </nav>

        <a
          href="#contact"
          onClick={scrollTo("#contact")}
          className="group hidden sm:inline-flex ml-1 items-center justify-center h-9 pl-4 pr-3 gap-1.5 rounded-full bg-[#0a0a0a] text-[#fbf9f4] text-[13px] font-semibold hover:bg-[#1a1a1a] hover:shadow-[0_6px_18px_-8px_rgba(10,10,10,0.4)] transition-all duration-150"
          data-testid="navbar-primary-cta-button"
        >
          Get in touch
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#facc15] text-[#0a0a0a] btn-arrow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-3"
              aria-hidden
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </span>
        </a>

        {/* mobile */}
        <div className="ml-auto sm:hidden flex items-center gap-1">
          <a
            href="#contact"
            onClick={scrollTo("#contact")}
            className="inline-flex items-center justify-center h-9 px-3.5 rounded-full bg-[#0a0a0a] text-[#fbf9f4] text-[12.5px] font-semibold"
            data-testid="navbar-mobile-primary-cta-button"
          >
            Get in touch
          </a>
          <button
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-[rgba(10,10,10,0.06)]"
            data-testid="navbar-mobile-menu-button"
          >
            {mobileOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen ? (
        <div
          className="pointer-events-auto absolute top-[64px] left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[680px] sm:hidden rounded-2xl border border-[rgba(10,10,10,0.08)] bg-white shadow-[0_16px_32px_-16px_rgba(10,10,10,0.2)] p-2"
          data-testid="navbar-mobile-sheet"
        >
          <a
            href="#what"
            onClick={scrollTo("#what")}
            className="block px-3 py-3 rounded-xl text-sm font-medium text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
            data-testid="navbar-mobile-link-what"
          >
            What we do
          </a>
          <a
            href="#work"
            onClick={scrollTo("#work")}
            className="block px-3 py-3 rounded-xl text-sm font-medium text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
            data-testid="navbar-mobile-link-work"
          >
            Work
          </a>
          <a
            href="#details"
            onClick={scrollTo("#details")}
            className="block px-3 py-3 rounded-xl text-sm font-medium text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
            data-testid="navbar-mobile-link-details"
          >
            Details
          </a>
          <a
            href="#contact"
            onClick={scrollTo("#contact")}
            className="block px-3 py-3 rounded-xl text-sm font-medium text-[#0a0a0a] hover:bg-[rgba(10,10,10,0.04)]"
            data-testid="navbar-mobile-link-contact"
          >
            Contact
          </a>
        </div>
      ) : null}
    </div>
  );
}
